// Airdrop farming (spec 8.1): protocols with hidden fates, tasks that earn points,
// sybil detection, and TGEs that pay out a real tradeable token (or "season 2 🙏").

import { TUNABLES } from "@degen/shared/config/tunables";
import { PROTOCOL_NAMES } from "@degen/shared/config/content";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { playerClout } from "../player";
import { createToken, mintTo, seedAmm } from "../market/token";
import { money } from "@degen/shared/money";


const A = TUNABLES.airdrops;

export type FarmTask = "testnet" | "bridge" | "swap" | "social" | "checkin";
export const FARM_TASKS: FarmTask[] = ["testnet", "bridge", "swap", "social", "checkin"];

export type ProtocolStatus = "live" | "paid" | "sybil" | "season2" | "exploited";

export interface Protocol {
  id: string;
  name: string;
  bornDay: number;
  // hidden from the player
  willAirdrop: boolean;
  tgeDay: number;
  allocationPoolUsd: number;
  sybilStrictness: number;
  // visible
  npcPoints: number;
  playerPoints: number;
  checkinStreak: number;
  lastCheckinDay: number;
  status: ProtocolStatus;
  tokenId?: string;
  payoutUsd?: number;
  tier?: AirdropTier; // the luck draw result
}

export type AirdropTier = "none" | "small" | "big" | "jackpot";

/** Player-facing odds for the TGE draw, given current points. */
export function airdropOdds(pr: Protocol): Record<AirdropTier, number> | null {
  if (pr.playerPoints < A.minEligiblePoints) return null;
  const luck = Math.min(1, pr.playerPoints / A.luckPointsForMax);
  const out = {} as Record<AirdropTier, number>;
  let total = 0;
  for (const k of ["none", "small", "big", "jackpot"] as AirdropTier[]) {
    const [a, b] = A.tiers[k].odds;
    out[k] = a + (b - a) * luck;
    total += out[k];
  }
  for (const k in out) out[k as AirdropTier] /= total;
  return out;
}

export interface AirdropState {
  protocols: Protocol[]; // live + recently finished
  nextId: number;
  sybilScore: number;
  today: Record<string, FarmTask[]>; // protocolId -> tasks done today (reset daily)
  socialFrac: number;
}

export function createAirdrops(): AirdropState {
  return { protocols: [], nextId: 1, sybilScore: 0, today: {}, socialFrac: 0 };
}

function spawnProtocol(state: GameState, rng: Rng, day: number, tgeIn: readonly [number, number] = A.tgeInDays) {
  const a = state.airdrops;
  const used = new Set(a.protocols.map((p) => p.name));
  let name = rng.pick(PROTOCOL_NAMES);
  for (let i = 0; i < 10 && used.has(name); i++) name = rng.pick(PROTOCOL_NAMES);
  a.protocols.push({
    id: `pr${a.nextId++}`,
    name,
    bornDay: day,
    willAirdrop: rng.chance(A.willAirdropChance),
    tgeDay: day + rng.int(tgeIn[0], tgeIn[1]),
    allocationPoolUsd: Math.round(rng.range(A.allocationPoolUsd[0], A.allocationPoolUsd[1])),
    sybilStrictness: rng.range(A.sybilStrictness[0], A.sybilStrictness[1]),
    npcPoints: Math.round(rng.range(100, 1_500)), // early farmers already in
    playerPoints: 0,
    checkinStreak: 0,
    lastCheckinDay: 0,
    status: "live",
  });
}

export function liveProtocols(state: GameState): Protocol[] {
  return state.airdrops.protocols.filter((p) => p.status === "live");
}

/** Fuzzy, player-facing TGE rumor (the exact day stays hidden). */
export function tgeRumor(p: Protocol, day: number): string {
  const d = p.tgeDay - day;
  if (d <= 1) return "TGE imminent 👀";
  if (d <= 4) return "TGE rumored this week";
  if (d <= 9) return "TGE rumored in ~1-2 weeks";
  return "TGE: wen? 🤷";
}

export type FarmResult = { ok: true; points: number } | { ok: false; error: string };

export function doFarmTask(state: GameState, protocolId: string, task: FarmTask): FarmResult {
  const p = state.player;
  const pr = state.airdrops.protocols.find((x) => x.id === protocolId && x.status === "live");
  const cfg = A.tasks[task];
  if (!pr) return { ok: false, error: "That protocol isn't live." };
  if (!cfg) return { ok: false, error: "Unknown task." };
  const today = (state.airdrops.today[pr.id] ??= []);
  // Each task once per protocol per day (energy used to be the limiter).
  if (today.includes(task)) return { ok: false, error: task === "checkin" ? "Already checked in today." : "Done for today on this protocol. Come back tomorrow." };
  if (p.cash < cfg.minCash) return { ok: false, error: `Bridging needs at least ${money(cfg.minCash)} in cash.` };
  if (p.cash < cfg.costUsd) return { ok: false, error: "Not enough cash for gas." };
  p.cash -= cfg.costUsd;
  let pts = cfg.points;
  const day = dayOf(state.tick);
  if (task === "checkin") {
    pr.checkinStreak = pr.lastCheckinDay === day - 1 ? pr.checkinStreak + 1 : 1;
    pr.lastCheckinDay = day;
    if (pr.checkinStreak > A.checkinStreakDays) pts *= A.checkinStreakMult;
  }
  if (task === "social") {
    // Quote-posting the protocol gets you a little Chirp exposure too.
    const reach = Math.pow(playerClout(p) + TUNABLES.social.baseReach, TUNABLES.social.reachExp);
    const gain = reach * A.socialQuestFollowers * TUNABLES.social.conversion * (p.trust / 100) + state.airdrops.socialFrac;
    p.followers += Math.floor(gain);
    state.airdrops.socialFrac = gain - Math.floor(gain);
  }
  if (state.pets.owned.some((x) => x.kind === "hamster" && x.happiness >= TUNABLES.pets.perkMinHappiness)) pts *= 1 + TUNABLES.pets.kinds.hamster.amount;
  pr.playerPoints += pts;
  today.push(task);
  return { ok: true, points: pts };
}

/** Day boundary, BEFORE daily counters reset: sybil check, NPC farming, TGEs, new protocols. */
export function airdropsNewDay(state: GameState, rng: Rng, day: number) {
  const a = state.airdrops;

  // Sybil: lots of protocols in one day, all with low-effort tasks only.
  const farmed = Object.keys(a.today);
  const lowEffortOnly = farmed.length > 0 && farmed.every((id) => a.today[id].every((t) => A.tasks[t].lowEffort));
  if (farmed.length > A.sybilMaxProtocolsPerDay && lowEffortOnly) {
    a.sybilScore += 1;
    pushLog(state, "sybil", "Farming many protocols with low-effort tasks looks sybil-ish. Careful.");
  } else a.sybilScore = Math.max(0, a.sybilScore - A.sybilDecayPerCleanDay);
  a.today = {};

  for (const pr of liveProtocols(state)) {
    pr.npcPoints += Math.round(rng.range(A.npcPointsPerDay[0], A.npcPointsPerDay[1]));
    if (day >= pr.tgeDay) runTge(state, rng, pr);
  }

  // Keep 6-8 protocols live; drop old finished ones (keep the last 6 for history).
  const want = rng.int(A.liveProtocols[0], A.liveProtocols[1]);
  while (liveProtocols(state).length < want) spawnProtocol(state, rng, day);
  const done = a.protocols.filter((p) => p.status !== "live");
  if (done.length > 6) {
    const drop = new Set(done.slice(0, done.length - 6).map((p) => p.id));
    a.protocols = a.protocols.filter((p) => !drop.has(p.id));
  }
}

function tickerFor(state: GameState, name: string): string {
  const base = name.replace(/[^A-Za-z]/g, "").toUpperCase();
  const used = new Set(Object.values(state.market.tokens).map((t) => t.ticker));
  for (const len of [4, 3, 5, 6]) {
    const t = base.slice(0, len);
    if (t.length >= 2 && !used.has(t)) return t;
  }
  return `${base.slice(0, 3)}${state.market.nextTokenId % 100}`;
}

function runTge(state: GameState, rng: Rng, pr: Protocol) {
  const a = state.airdrops;
  if (!pr.willAirdrop) {
    pr.status = "season2";
    pushLog(state, "season2", `${pr.name}: "points program season 2 🙏". Your points are worthless.`, { protocol: pr.id });
    return;
  }
  const flagChance = pr.sybilStrictness * Math.min(1, a.sybilScore / A.sybilScoreForMaxRisk);
  const flagged = pr.playerPoints > 0 && rng.chance(flagChance);
  // One shot, luck decides: eligible farmers draw a tier (more points = better odds).
  const odds = flagged ? null : airdropOdds(pr);
  const tier: AirdropTier = odds ? (rng.weighted(odds) as AirdropTier) : "none";
  const [lo, hi] = A.tiers[tier].share;
  const payout = tier === "none" ? 0 : pr.allocationPoolUsd * rng.range(lo, hi);
  const share = payout / pr.allocationPoolUsd;
  pr.tier = tier;

  // The airdrop token: 10% of supply airdropped, already trading on an AMM.
  const m = state.market;
  const t = createToken({
    id: `t${m.nextTokenId++}`,
    name: pr.name,
    ticker: tickerFor(state, pr.name),
    creator: `protocol:${pr.id}`,
    description: `${pr.name} governance token. Airdropped to farmers.`,
    iconSeed: rng.seed(),
    fate: "normal",
    hype: rng.range(40, 70),
    bornTick: state.tick,
  });
  const airdropTokens = t.totalSupply * A.tgeAirdropShare;
  const price = pr.allocationPoolUsd / airdropTokens;
  seedAmm(t, pr.allocationPoolUsd * A.tgeLiquidityShare, price, state.tick);
  m.tokens[t.id] = t;
  m.stats.spawned++;
  pr.tokenId = t.id;

  if (payout > 0) {
    mintTo(t, "player", payout / price);
    state.player.holdings.push({ kind: "token", tokenId: t.id, costUsd: 0 });
  }
  // NPC farmers get their share; many dump it within hours.
  const farmers = state.npcs.filter((n) => n.archetype === "farmer");
  const npcTokens = (airdropTokens * (1 - share)) * A.tgeNpcDumpShare;
  for (let i = 0; i < A.tgeNpcDumpers && farmers.length; i++) {
    const n = rng.pick(farmers);
    const amt = npcTokens / A.tgeNpcDumpers;
    mintTo(t, n.id, amt);
    n.pos[t.id] = { entry: price, peak: price, tick: state.tick, hold: rng.int(5, 180), target: 0, tookProfit: false };
  }

  pr.status = flagged ? "sybil" : "paid";
  pr.payoutUsd = payout;
  const usd = money(Math.round(payout));
  const farmed = pr.playerPoints > 0 ? 1 : 0;
  if (flagged) pushLog(state, "sybil_flagged", `${pr.name} TGE: you were flagged as a sybil. Allocation: 0. 💀`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else if (tier === "jackpot") pushLog(state, "tge", `🎰 JACKPOT! ${pr.name} airdropped you ${usd} of $${t.ticker}!`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else if (tier === "big") pushLog(state, "tge", `🎉 Big allocation! ${pr.name} airdropped you ${usd} of $${t.ticker}.`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else if (tier === "small") pushLog(state, "tge", `✅ ${pr.name} airdropped you ${usd} of $${t.ticker}. Small, but it's yours.`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else if (farmed && pr.playerPoints < A.minEligiblePoints) pushLog(state, "tge", `${pr.name} TGE: you only had ${Math.round(pr.playerPoints)} points, not eligible (need ${A.minEligiblePoints}).`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else if (farmed) pushLog(state, "tge", `😢 ${pr.name} TGE: unlucky, no allocation this time. On to the next one.`, { protocol: pr.id, tokenId: t.id, mine: 1 });
  else pushLog(state, "tge", `${pr.name} launched $${t.ticker}. You didn't farm it.`, { protocol: pr.id, tokenId: t.id });
}

/** Initial protocols when a world is created. */
export function seedProtocols(state: GameState, rng: Rng) {
  const n = rng.int(A.liveProtocols[0], A.liveProtocols[1]);
  // SPEC-NOTE: the world was already farming before you showed up, so starting protocols TGE sooner.
  for (let i = 0; i < n; i++) spawnProtocol(state, rng, 1, A.initialTgeInDays);
}
